import React, { useRef, useState, useCallback, ReactNode, CSSProperties } from 'react';
import styles from './Button.css';

interface ButtonProps {
    onPress: () => void;
    onLongPress?: () => void;
    activeOpacity?: number;
    style?: CSSProperties;
    className?: string;
    children: ReactNode;
}

const Button: React.FC<ButtonProps> = ({ 
    onPress, 
    onLongPress, 
    activeOpacity = 0.6, 
    style, 
    className,
    children 
}) => {
    const [isPressed, setIsPressed] = useState(false);
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressInterval = useRef<ReturnType<typeof setInterval> | null>(null);
    const longPressTriggered = useRef(false);

    const clearLongPressTimers = useCallback(() => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
        if (longPressInterval.current) {
            clearInterval(longPressInterval.current);
            longPressInterval.current = null;
        }
    }, []);

    const handlePointerDown = useCallback((event: React.PointerEvent) => {
        event.preventDefault();
        setIsPressed(true);
        longPressTriggered.current = false;

        if (onLongPress) {
            longPressTimer.current = setTimeout(() => {
                longPressTriggered.current = true;
                onLongPress();
                longPressInterval.current = setInterval(() => {
                    onLongPress();
                }, 500);
            }, 500);
        }
    }, [onLongPress]);

    const handlePointerUp = useCallback((event: React.PointerEvent) => {
        event.preventDefault();
        setIsPressed(false);
        clearLongPressTimers();

        if (!longPressTriggered.current) {
            onPress();
        }
    }, [onPress, clearLongPressTimers]);

    const handlePointerLeave = useCallback(() => {
        setIsPressed(false);
        clearLongPressTimers();
    }, [clearLongPressTimers]);

    const handlePointerCancel = useCallback(() => {
        setIsPressed(false);
        clearLongPressTimers();
    }, [clearLongPressTimers]);

    return (
        <div
            className={`${styles.button} ${className || ''}`}
            style={{
                ...style,
                opacity: isPressed ? activeOpacity : 1,
            }}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            onPointerCancel={handlePointerCancel}
        >
            {children}
        </div>
    );
};

export default Button;
